import { externalgetApi, externalpostApi, externalputApi } from "./externalApis.tsx";
import externaldeletecomApi from "./externalApis.tsx";
import {
  ITEM_IMPORT_STAGING_API,
  ITEM_IMPORT_STAGING_PREVIEW,
  ITEM_IMPORT_STAGING_FINALIZE,
  ITEM_IMPORT_STAGING_VALIDATE,
} from "../api/ApiModel.tsx";
import {
  MSG_ITEM_RECORD_EXISTS,
  MSG_MANDATORY_FIELDS_MISSING,
  MSG_SUBMISSION_FAILED,
} from "../utils/sys/Messages.tsx";

export interface StagingRow {
  stagingcode: number;
  refno: string;
  batchcode: number;
  brandname?: string;
  maincategory?: string;
  category?: string;
  subcategory?: string;
  genericname?: string;
  itemname?: string;
  packagetype?: string;
  measuringunit?: string;
  modelname?: string;
  packageunits?: string;
  descrip?: string;
  isselling?: string | null;
  isinv?: string | null;
  variantdata?: string;
  recordstatus?: string;
  validationerrors?: string;
  duplicatesource?: string;
  sourcerowno?: number;
}

/** Normalize a value to Y/N for varchar(1) flags; accepts Y/N or Yes/No. */
export const normalizeYN = (v: unknown): string | null => {
  const s = String(v ?? "").trim().toUpperCase();
  if (s === "Y" || s === "YES") return "Y";
  if (s === "N" || s === "NO") return "N";
  return null;
};

/** Display/export label for Y/N flags (Y → Yes, N → No; missing → N/A). */
export const formatYNDisplay = (v: unknown): string => {
  const s = String(v ?? "").trim().toUpperCase();
  if (s === "Y") return "Yes";
  if (s === "N") return "No";
  return "N/A";
};

export interface ValidateRow {
  sourcerowno: number;
  recordstatus?: string;
  validationerrors?: string;
}

export interface ValidateResponse {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  duplicateRows: number;
  rows: ValidateRow[];
}

export interface FinalizeRowPayload {
  sourcerowno: number;
  brandname: string;
  maincategory: string;
  category: string;
  subcategory: string;
  genericname: string;
  itemname: string;
  packagetype: string;
  measuringunit: string;
  modelname: string;
  packageunits: string;
  descrip: string;
  isselling?: string | null;
  isinv?: string | null;
  variantdata: string;
  validationerrors?: string;
  duplicatesource?: string;
}

export interface FinalizePayload {
  compcode: string;
  loccode: string;
  enuser: string;
  filename: string;
  filetype: string;
  validItems: FinalizeRowPayload[];
  excludedValidRows: FinalizeRowPayload[];
  invalidRows: FinalizeRowPayload[];
  duplicateRows: FinalizeRowPayload[];
}

export interface FinalizeResponse {
  batchcode: number;
  batchrefno: string;
  totalRows: number;
  importedRows: number;
  unpostedRows: number;
  invalidRows: number;
  duplicateRows: number;
  message?: string;
}

/** Read-only DB validation (no staging writes). */
export const previewValidateImportRows = (payload: object): Promise<ValidateResponse> =>
  externalpostApi<ValidateResponse>(ITEM_IMPORT_STAGING_PREVIEW, payload);

/** Alias kept for any legacy callers; same as preview-validate. */
export const validateImportRows = (payload: object): Promise<ValidateResponse> =>
  externalpostApi<ValidateResponse>(ITEM_IMPORT_STAGING_VALIDATE, payload);

/** Atomic finalize: import valid rows + persist INVALID/DUPLICATE/UNPOSTED staging. */
export const finalizeImport = (payload: FinalizePayload): Promise<FinalizeResponse> =>
  externalpostApi<FinalizeResponse>(ITEM_IMPORT_STAGING_FINALIZE, payload);

export const getUnverifiedStagingRows = (
  compCode: string,
  locCode: string,
  enUser: string
): Promise<StagingRow[]> =>
  externalgetApi<StagingRow[]>(ITEM_IMPORT_STAGING_API, "GET", {
    COMPCODE: compCode,
    LOCCODE: locCode,
    ENUSER: enUser,
  });

export const getStagingRowByRef = (
  refNo: string,
  compCode: string,
  locCode: string
): Promise<StagingRow> =>
  externalgetApi<StagingRow>(`${ITEM_IMPORT_STAGING_API}/${refNo}`, "GET", {
    COMPCODE: compCode,
    LOCCODE: locCode,
  });

export const updateStagingRow = (refNo: string, payload: object): Promise<StagingRow> =>
  externalputApi<StagingRow>(`${ITEM_IMPORT_STAGING_API}/${refNo}`, payload);

export const submitStagingRow = (refNo: string, payload: object): Promise<{ message: string }> =>
  externalpostApi(`${ITEM_IMPORT_STAGING_API}/${refNo}/submit`, payload);

export const deleteStagingRow = (
  refNo: string,
  compCode: string,
  locCode: string,
  enUser: string
): Promise<{ message: string }> =>
  externaldeletecomApi(`${ITEM_IMPORT_STAGING_API}/${refNo}`, {
    COMPCODE: compCode,
    LOCCODE: locCode,
    ENUSER: enUser,
  });

export const parseApiError = (error: any): string => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const message =
    typeof data === "string" ? data : data?.message || error?.message;

  if (message) return message;

  if (status === 409) return MSG_ITEM_RECORD_EXISTS;
  if (status === 400) return MSG_MANDATORY_FIELDS_MISSING;
  if (status === 500) return MSG_SUBMISSION_FAILED;

  return "Unknown error";
};
